import { Button, Icon, InitialsAvatar, SearchBar, Separator, Spinner } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronDown, TbUsers } from "react-icons/tb";

import { AddMembersModal, SelectedUserWithRoleById } from "@/components/teams/add-members-modal";
import { PAGINATION } from "@/constants/pagination";
import {
    useAgentSeatAssignments,
    useCreateAgentSeatAssignment,
    useDeleteAgentSeatAssignment,
    useUpdateAgentSeatAssignment,
} from "@/hooks/queries/use-agent-seat-assignment.queries";
import { useInfiniteUsers, useUsersByIdsBatch } from "@/hooks/queries/user.queries";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn } from "@/lib/utils";
import { useUserStatusContext } from "@/providers/general/user-status.provider";
import { UserStatus } from "@/types/user-statuses.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { getStatusCircleColor, getStatusLegendItems } from "@/utils/user-status";

import { AgentInformationPanel } from "./components/agent-information-panel";
import { AgentSettingsDialog } from "./components/agent-settings-dialog";
import { CurrentAgentsStatus } from "./components/current-agents-status";
import {
    type AgentProps,
    Cable,
    type CableProps,
    Label,
    type LabelProps,
    Table,
    TableProps,
    Wall,
    type WallProps,
} from "./components/map";
import { defaultCables, defaultLabels, defaultTables, defaultWalls } from "./components/map/definitions";

export type AgentsInformation = {
    id: string;
    table: string;
    seat: number;
    status: UserStatus;
};

const initialViewBox = { x: -1100, y: -640, width: 2190, height: 1200 };

export const Route = createFileRoute(APP_ROUTES_FULL.MANAGEMENT_USERS)({
    component: ManagementUsers,
    staticData: {
        config: {
            breadcrumb: {
                title: "users",
                to: APP_ROUTES_TO.MANAGEMENT_USERS,
            },
            title: "users",
        },
    },
});

function ManagementUsers() {
    const { t } = useTranslation();

    // Refs for movement to avoid rerenders
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const viewBoxRef = useRef(initialViewBox);
    const isDraggingRef = useRef(false);
    const dragStartRef = useRef({ x: 0, y: 0 });
    const scaleRef = useRef(1);
    const displayScaleRef = useRef(1);
    const lastPinchDistanceRef = useRef(0);
    const zoomIndicatorRef = useRef<HTMLSpanElement>(null);
    const unassignedUsersContainerRef = useRef<HTMLDivElement>(null);
    const unassignedUsersScrollRef = useRef<HTMLDivElement>(null);

    const [walls] = useState<WallProps[]>(defaultWalls);
    const [labels] = useState<LabelProps[]>(defaultLabels);
    const [tables] = useState<TableProps[]>(defaultTables);
    const [cables] = useState<CableProps[]>(defaultCables);

    const [selectedAgent, setSelectedAgent] = useState<AgentProps | null>(null);
    const [selectedSeat, setSelectedSeat] = useState<{ tableId: string; seatNumber: number; agentId?: string } | null>(
        null
    );

    // Modal state
    const [showAddMembersModal, setShowAddMembersModal] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [isAgentPanelOpen, setIsAgentPanelOpen] = useState(false);

    // Separate search state for unassigned users list
    const [unassignedUsersSearchQuery, setUnassignedUsersSearchQuery] = useState("");
    // Reset currentPage when userSearchQuery changes (for modal pagination)
    useEffect(() => {
        setCurrentPage(1);
    }, [userSearchQuery]);

    // Reset search query when modal closes (clean state for next open)
    useEffect(() => {
        if (!showAddMembersModal) {
            setUserSearchQuery("");
            setCurrentPage(1);
        }
    }, [showAddMembersModal]);

    // Mutations
    const createAssignment = useCreateAgentSeatAssignment();
    const updateAssignment = useUpdateAgentSeatAssignment();
    const deleteAssignment = useDeleteAgentSeatAssignment();

    const { data: assignments = [] } = useAgentSeatAssignments();

    // Separate query for modal with its own search
    const debouncedModalSearch = useDebouncedValue(userSearchQuery);
    const { data: modalUsersResponse, isLoading: isLoadingModalUsers } = useInfiniteUsers(
        {
            search: debouncedModalSearch || undefined,
            limit: PAGINATION.PAGE_SIZE,
        },
        {
            enabled: showAddMembersModal, // Only fetch when modal is open
        }
    );

    // Flatten modal users
    const modalUsersData = useMemo(() => {
        return modalUsersResponse?.pages.flatMap((page) => page.users) ?? [];
    }, [modalUsersResponse]);

    // User statuses
    const { userStatuses } = useUserStatusContext();

    // Filter out users that are already assigned to seats (memoized)
    const assignedUserIds = useMemo(() => assignments.map((assignment) => assignment.agentId), [assignments]);

    const assignedUserIdsSet = useMemo(() => new Set(assignedUserIds), [assignedUserIds]);

    // Filter modal users for unassigned (for modal)
    const modalUnassignedUsers = useMemo(
        () => modalUsersData.filter((user) => !assignedUserIdsSet.has(user.id)),
        [modalUsersData, assignedUserIdsSet]
    );

    // Client-side pagination for modal unassigned users
    const { unassignedUsers, unassignedUsersTotal } = useMemo(() => {
        const startIndex = (currentPage - 1) * 10;
        const endIndex = startIndex + 10;
        return {
            unassignedUsers: modalUnassignedUsers.slice(startIndex, endIndex),
            unassignedUsersTotal: modalUnassignedUsers.length,
        };
    }, [modalUnassignedUsers, currentPage]);

    // Find users who have status but no seat assignment (memoized)
    const unassignedUsersWithStatus = useMemo(
        () => Object.keys(userStatuses).filter((userId) => !assignedUserIdsSet.has(userId)),
        [userStatuses, assignedUserIdsSet]
    );

    // Load minimal user info for unassigned users via auth-svc /user/batch
    const { data: unassignedUsersData = [], isLoading: isLoadingUnassignedUsers } =
        useUsersByIdsBatch(unassignedUsersWithStatus);

    // Apply client-side search over unassigned users
    const filteredUnassignedUsers = useMemo(() => {
        const query = unassignedUsersSearchQuery.trim().toLowerCase();
        if (!query) return unassignedUsersData;
        return unassignedUsersData.filter((user) => {
            const name = (user.name ?? "").toLowerCase();
            const email = (user.email ?? "").toLowerCase();
            return name.includes(query) || email.includes(query);
        });
    }, [unassignedUsersData, unassignedUsersSearchQuery]);

    // Toggle state for unassigned users section
    const [showUnassignedUsers, setShowUnassignedUsers] = useState(false);

    // Helper: Map assignments to table/seat for Table component (memoized)
    const getSeatAgentsInformation = useCallback(
        (tableId: string, tableSize: number) => {
            // Map seatNumber (1-based) to agent info
            const seatInfo: Record<number, AgentsInformation> = {};
            for (let i = 1; i <= tableSize; i++) {
                const assignment = assignments.find((a) => a.tableId === tableId && a.seatNumber === i);
                if (assignment) {
                    const userStatus = userStatuses[assignment.agentId] || UserStatus.OFFLINE;

                    seatInfo[i] = {
                        id: assignment.agentId,
                        table: assignment.tableId,
                        seat: assignment.seatNumber,
                        status: userStatus,
                    };
                }
            }
            return seatInfo;
        },
        [assignments, userStatuses]
    );

    const handleAgentClick = useCallback((agent: AgentProps) => {
        setSelectedAgent(agent);
        setIsAgentPanelOpen(true);
    }, []);

    const handleUnassignedUserClick = useCallback(
        (user: { id: string; name?: string | null; email: string }) => {
            setSelectedAgent({
                id: 0, // Synthetic id for unassigned users
                x: 0,
                y: 0,
                assigned: false,
                state: userStatuses[user.id] || UserStatus.OFFLINE,
                userId: user.id,
            });
            setSelectedSeat(null); // Clear seat selection since this is an unassigned user
            setIsAgentPanelOpen(true);
        },
        [userStatuses]
    );

    const handleSeatClick = useCallback((tableId: string, seatNumber: number, agentId?: string) => {
        setSelectedSeat({ tableId, seatNumber, agentId });
        if (!agentId) {
            setShowAddMembersModal(true);
        }
    }, []);

    const handleDeleteAssignment = useCallback(async () => {
        if (!selectedSeat) return false;
        return await deleteAssignment.mutateAsync({
            tableId: selectedSeat.tableId,
            seatNumber: selectedSeat.seatNumber,
        });
    }, [selectedSeat, deleteAssignment]);

    const handleAddMembers = useCallback(
        async (users: SelectedUserWithRoleById[]) => {
            if (!selectedSeat || users.length === 0) return;

            try {
                const user = users[0]; // We only assign one user per seat
                const { tableId, seatNumber, agentId } = selectedSeat;

                if (agentId) {
                    // Update existing assignment
                    await updateAssignment.mutateAsync({
                        tableId,
                        seatNumber,
                        update: { agentId: user.userId },
                    });
                } else {
                    // Create new assignment
                    await createAssignment.mutateAsync({
                        tableId,
                        seatNumber,
                        agentId: user.userId,
                    });
                }

                setShowAddMembersModal(false);
                setSelectedSeat(null);
                setSelectedAgent(null); // Close the info panel
            } catch (error) {
                console.error("Error assigning agent to seat:", error);
            }
        },
        [selectedSeat, updateAssignment, createAssignment]
    );

    // Update SVG viewBox directly (memoized)
    const updateViewBox = useCallback(() => {
        if (svgRef.current) {
            const { x, y, width, height } = viewBoxRef.current;
            svgRef.current.setAttribute("viewBox", `${x} ${y} ${width} ${height}`);
        }
    }, []);

    // Update control box (memoized)
    const updateZoomIndicator = useCallback(() => {
        const zoomIndicator = zoomIndicatorRef.current;
        if (zoomIndicator) {
            zoomIndicator.textContent = `${Math.round(displayScaleRef.current * 100)}%`;
        }
    }, []);

    // Pan and Zoom handlers using refs (memoized)
    const handleMouseDown = useCallback((event: React.MouseEvent) => {
        if (event.button === 0) {
            // Left mouse button
            isDraggingRef.current = true;
            dragStartRef.current = { x: event.clientX, y: event.clientY };
        }
    }, []);

    const handleMouseMove = useCallback(
        (event: React.MouseEvent) => {
            if (!isDraggingRef.current) return;

            const panSensitivity = 3; // Increase pan sensitivity
            const deltaX = ((event.clientX - dragStartRef.current.x) / scaleRef.current) * panSensitivity;
            const deltaY = ((event.clientY - dragStartRef.current.y) / scaleRef.current) * panSensitivity;

            viewBoxRef.current = {
                ...viewBoxRef.current,
                x: viewBoxRef.current.x - deltaX,
                y: viewBoxRef.current.y - deltaY,
            };

            updateViewBox();
            updateZoomIndicator();
            dragStartRef.current = { x: event.clientX, y: event.clientY };
        },
        [updateViewBox, updateZoomIndicator]
    );

    const handleMouseUp = useCallback(() => {
        isDraggingRef.current = false;
    }, []);

    const resetView = useCallback(() => {
        viewBoxRef.current = initialViewBox;
        scaleRef.current = 1;
        displayScaleRef.current = 1;
        updateViewBox();
        updateZoomIndicator();
    }, [updateViewBox, updateZoomIndicator]);

    // Zoom calculation helpers (memoized - pure functions)
    const calculateZoomFactor = useCallback((deltaY: number) => {
        return deltaY > 0 ? 0.92 : 1.08;
    }, []);

    const calculateNewScale = useCallback((currentScale: number, zoomFactor: number) => {
        return Math.max(0.8, Math.min(3, currentScale * zoomFactor));
    }, []);

    const getMousePositionRelativeToContainer = useCallback((event: WheelEvent, container: HTMLElement) => {
        const rect = container.getBoundingClientRect();
        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        };
    }, []);

    const convertToViewBoxCoordinates = useCallback((mouseX: number, mouseY: number, container: HTMLElement) => {
        const rect = container.getBoundingClientRect();
        return {
            x: (mouseX / rect.width) * viewBoxRef.current.width + viewBoxRef.current.x,
            y: (mouseY / rect.height) * viewBoxRef.current.height + viewBoxRef.current.y,
        };
    }, []);

    const calculateNewViewBoxDimensions = useCallback((newScale: number) => {
        return {
            width: initialViewBox.width / newScale,
            height: initialViewBox.height / newScale,
        };
    }, []);

    const calculateNewViewBoxPosition = useCallback(
        (
            svgX: number,
            svgY: number,
            mouseX: number,
            mouseY: number,
            container: HTMLElement,
            newWidth: number,
            newHeight: number
        ) => {
            const rect = container.getBoundingClientRect();
            return {
                x: svgX - (mouseX / rect.width) * newWidth,
                y: svgY - (mouseY / rect.height) * newHeight,
            };
        },
        []
    );

    const handleZoom = useCallback(
        (event: WheelEvent, container: HTMLElement) => {
            event.preventDefault();
            event.stopPropagation();

            const zoomFactor = calculateZoomFactor(event.deltaY);
            const newScale = calculateNewScale(scaleRef.current, zoomFactor);

            const { x: mouseX, y: mouseY } = getMousePositionRelativeToContainer(event, container);
            const { x: svgX, y: svgY } = convertToViewBoxCoordinates(mouseX, mouseY, container);
            const { width: newWidth, height: newHeight } = calculateNewViewBoxDimensions(newScale);
            const { x: newX, y: newY } = calculateNewViewBoxPosition(
                svgX,
                svgY,
                mouseX,
                mouseY,
                container,
                newWidth,
                newHeight
            );

            scaleRef.current = newScale;
            viewBoxRef.current = { x: newX, y: newY, width: newWidth, height: newHeight };

            updateViewBox();
            displayScaleRef.current = newScale;
            updateZoomIndicator();
        },
        [
            calculateZoomFactor,
            calculateNewScale,
            getMousePositionRelativeToContainer,
            convertToViewBoxCoordinates,
            calculateNewViewBoxDimensions,
            calculateNewViewBoxPosition,
            updateViewBox,
            updateZoomIndicator,
        ]
    );

    // Add native wheel event listener with passive: false
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleWheelNative = (event: WheelEvent) => {
            handleZoom(event, container);
        };

        container.addEventListener("wheel", handleWheelNative, { passive: false });

        return () => {
            container.removeEventListener("wheel", handleWheelNative);
        };
    }, [handleZoom]);

    // Add touch event listeners for mobile pan and pinch-to-zoom
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleTouchStart = (event: TouchEvent) => {
            if (event.touches.length === 1) {
                isDraggingRef.current = true;
                dragStartRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
            } else if (event.touches.length === 2) {
                isDraggingRef.current = false;
                const dx = event.touches[0].clientX - event.touches[1].clientX;
                const dy = event.touches[0].clientY - event.touches[1].clientY;
                lastPinchDistanceRef.current = Math.hypot(dx, dy);
            }
        };

        const handleTouchMove = (event: TouchEvent) => {
            event.preventDefault();

            if (event.touches.length === 1 && isDraggingRef.current) {
                const touch = event.touches[0];
                const panSensitivity = 5;
                const deltaX = ((touch.clientX - dragStartRef.current.x) / scaleRef.current) * panSensitivity;
                const deltaY = ((touch.clientY - dragStartRef.current.y) / scaleRef.current) * panSensitivity;

                viewBoxRef.current = {
                    ...viewBoxRef.current,
                    x: viewBoxRef.current.x - deltaX,
                    y: viewBoxRef.current.y - deltaY,
                };

                updateViewBox();
                dragStartRef.current = { x: touch.clientX, y: touch.clientY };
            } else if (event.touches.length === 2 && lastPinchDistanceRef.current > 0) {
                const dx = event.touches[0].clientX - event.touches[1].clientX;
                const dy = event.touches[0].clientY - event.touches[1].clientY;
                const distance = Math.hypot(dx, dy);
                const zoomFactor = distance / lastPinchDistanceRef.current;
                const newScale = Math.max(0.8, Math.min(3, scaleRef.current * zoomFactor));

                const midX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
                const midY = (event.touches[0].clientY + event.touches[1].clientY) / 2;
                const rect = container.getBoundingClientRect();
                const svgX = ((midX - rect.left) / rect.width) * viewBoxRef.current.width + viewBoxRef.current.x;
                const svgY = ((midY - rect.top) / rect.height) * viewBoxRef.current.height + viewBoxRef.current.y;
                const newWidth = initialViewBox.width / newScale;
                const newHeight = initialViewBox.height / newScale;

                scaleRef.current = newScale;
                displayScaleRef.current = newScale;
                viewBoxRef.current = {
                    x: svgX - ((midX - rect.left) / rect.width) * newWidth,
                    y: svgY - ((midY - rect.top) / rect.height) * newHeight,
                    width: newWidth,
                    height: newHeight,
                };

                updateViewBox();
                updateZoomIndicator();
                lastPinchDistanceRef.current = distance;
            }
        };

        const handleTouchEnd = () => {
            isDraggingRef.current = false;
            lastPinchDistanceRef.current = 0;
        };

        container.addEventListener("touchstart", handleTouchStart, { passive: true });
        container.addEventListener("touchmove", handleTouchMove, { passive: false });
        container.addEventListener("touchend", handleTouchEnd, { passive: true });

        return () => {
            container.removeEventListener("touchstart", handleTouchStart);
            container.removeEventListener("touchmove", handleTouchMove);
            container.removeEventListener("touchend", handleTouchEnd);
        };
    }, [updateViewBox, updateZoomIndicator]);

    // Stop wheel events on unassigned users containers to prevent room plan zoom
    useEffect(() => {
        const unassignedContainer = unassignedUsersContainerRef.current;
        const scrollContainer = unassignedUsersScrollRef.current;

        const handleWheelStop = (event: WheelEvent) => {
            // Stop event propagation in capture phase to prevent it from reaching the room plan container
            event.stopPropagation();
            event.stopImmediatePropagation();
        };

        // Apply to both the outer container and the scrollable inner div
        if (unassignedContainer) {
            unassignedContainer.addEventListener("wheel", handleWheelStop, { capture: true, passive: false });
        }

        if (scrollContainer) {
            scrollContainer.addEventListener("wheel", handleWheelStop, { capture: true, passive: false });
        }

        return () => {
            if (unassignedContainer) {
                unassignedContainer.removeEventListener("wheel", handleWheelStop, { capture: true });
            }
            if (scrollContainer) {
                scrollContainer.removeEventListener("wheel", handleWheelStop, { capture: true });
            }
        };
    }, [showUnassignedUsers]); // Re-run when the section is shown/hidden

    const handleOpenSettingsModal = useCallback(() => {
        setShowSettingsModal(true);
    }, []);

    return (
        <div className="flex flex-1 min-h-0 w-full">
            <div className="flex flex-1 min-h-0 w-full flex-col">
                <div
                    ref={containerRef}
                    className="relative isolate flex flex-1 min-h-0 w-full items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-linear-to-br from-muted/50 via-muted/30 to-muted/10 shadow-inner select-none"
                >
                    <div className="absolute top-3 right-3 z-20 flex shrink-0 items-center overflow-hidden rounded-xl border border-border/60 bg-background/80 shadow-lg backdrop-blur-lg">
                        <div className="flex items-center gap-2 px-2.5 py-1.5 sm:px-3">
                            <div className="h-2 w-2 animate-pulse rounded-full bg-primary opacity-60"></div>
                            <span ref={zoomIndicatorRef} className="text-sm font-medium text-foreground/80">
                                {Math.round(displayScaleRef.current * 100)}%
                            </span>
                        </div>
                        <div className="h-6 w-px bg-border/60" />
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={resetView}
                            className="h-auto rounded-none px-2.5 py-1.5 hover:bg-muted/80 sm:px-3"
                        >
                            <span className="hidden sm:inline">{t("reset_view")}</span>
                            <span className="sm:hidden">{t("reset")}</span>
                        </Button>
                    </div>
                    <svg
                        ref={svgRef}
                        viewBox={`${initialViewBox.x} ${initialViewBox.y} ${initialViewBox.width} ${initialViewBox.height}`}
                        className={`w-full h-full bg-muted ${isDraggingRef.current ? "cursor-grabbing" : "cursor-grab"}`}
                        preserveAspectRatio="xMidYMid meet"
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    >
                        {/* Render Walls */}
                        {walls.map((wall) => (
                            <Wall key={wall.id} {...wall} />
                        ))}

                        {/* Render Cables */}
                        {cables.map((cable) => (
                            <Cable key={cable.id} {...cable} />
                        ))}

                        {/* Render Tables with Agents */}
                        {tables.map((table) => {
                            const seatAgentsInformation = getSeatAgentsInformation(table.id, table.size);
                            return (
                                <Table
                                    key={table.id}
                                    {...table}
                                    onAgentClick={handleAgentClick}
                                    onSeatClick={handleSeatClick}
                                    agentsInformation={seatAgentsInformation}
                                    selectedAgentId={selectedAgent?.id}
                                    selectedTableId={(() => {
                                        // Find which table the selected agent belongs to
                                        if (selectedAgent?.userId) {
                                            const assignment = assignments.find(
                                                (a) => a.agentId === selectedAgent.userId
                                            );
                                            return assignment?.tableId;
                                        }
                                        return undefined;
                                    })()}
                                />
                            );
                        })}

                        {/* Render Labels */}
                        {labels.map((label) => (
                            <Label key={label.id} {...label} />
                        ))}
                    </svg>

                    {/* Unassigned Users with Status Section */}
                    {unassignedUsersWithStatus.length > 0 && (
                        <div
                            ref={unassignedUsersContainerRef}
                            className="absolute left-2 top-3 z-10 max-w-[calc(100%-10.5rem)] overflow-hidden rounded-xl border border-border bg-background sm:max-w-[300px] sm:w-[300px]"
                        >
                            <Button
                                variant="ghost"
                                onClick={() => setShowUnassignedUsers(!showUnassignedUsers)}
                                className="flex h-auto w-full min-w-0 justify-between gap-2 rounded-none p-2 transition-all duration-200 hover:bg-muted/50"
                            >
                                <div className="flex min-w-0 items-center gap-2">
                                    <Icon icon={TbUsers} size={20} className="shrink-0" />
                                    <span className="truncate text-sm">
                                        {filteredUnassignedUsers.length} {t("unassigned_users")}
                                    </span>
                                </div>
                                <Icon
                                    icon={TbChevronDown}
                                    size={20}
                                    className={cn("shrink-0 transition-transform duration-200", {
                                        "-rotate-180": showUnassignedUsers,
                                    })}
                                />
                            </Button>

                            {showUnassignedUsers && (
                                <>
                                    <Separator />
                                    <div className="border-b bg-background p-2">
                                        <SearchBar
                                            value={unassignedUsersSearchQuery}
                                            onChange={setUnassignedUsersSearchQuery}
                                            placeholder={t("search_users")}
                                            className="w-full"
                                        />
                                    </div>
                                    <div
                                        ref={unassignedUsersScrollRef}
                                        className="max-h-[min(300px,40vh)] overflow-y-auto md:max-h-[300px]"
                                    >
                                        {isLoadingUnassignedUsers && filteredUnassignedUsers.length === 0 ? (
                                            <div className="flex items-center justify-center p-4">
                                                <Spinner size="sm" />
                                            </div>
                                        ) : filteredUnassignedUsers.length === 0 ? (
                                            <div className="text-center py-8 text-sm text-muted-foreground">
                                                {t("no_unassigned_users")}
                                            </div>
                                        ) : (
                                            <>
                                                {filteredUnassignedUsers.map((user) => {
                                                    const status = userStatuses[user.id] || UserStatus.OFFLINE;

                                                    // Convert status to translation key (e.g., "inCall" -> "in_call")
                                                    const statusKey = status
                                                        .replace(/([A-Z])/g, "_$1")
                                                        .toLowerCase()
                                                        .replace(/^_/, "");

                                                    return (
                                                        <div
                                                            role="button"
                                                            key={user.id}
                                                            onClick={() => handleUnassignedUserClick(user)}
                                                            className="group flex cursor-pointer items-center justify-between gap-2 border-b border-border p-2 transition-all duration-200 last:border-b-0 hover:bg-muted"
                                                        >
                                                            <div className="flex min-w-0 items-center gap-2">
                                                                <InitialsAvatar
                                                                    name={user.name || t("unknown")}
                                                                    size="sm"
                                                                />
                                                                <div className="flex min-w-0 flex-1 flex-col">
                                                                    <div className="truncate text-sm font-semibold">
                                                                        {user.name || t("unknown")}
                                                                    </div>
                                                                    {user.email && (
                                                                        <div className="truncate text-xs text-muted-foreground">
                                                                            {user.email}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex shrink-0 items-center gap-1.5">
                                                                <div
                                                                    className={`h-2.5 w-2.5 rounded-full ${getStatusCircleColor(status)}`}
                                                                />
                                                                <span className="hidden text-xs font-medium capitalize text-muted-foreground sm:inline">
                                                                    {t(statusKey)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    {/* Status legend + agent counts */}
                    <div className="absolute bottom-3 left-3 right-3 md:bottom-4 md:left-4 md:right-4">
                        <div className="relative rounded-xl border border-border/60 bg-background/80 p-2 shadow-lg backdrop-blur-lg">
                            <div className="flex flex-col justify-between gap-2 lg:flex-row lg:items-center">
                                <div className="hidden min-w-0 flex-1 md:block">
                                    <div className="mb-2 flex items-center gap-2 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                        <div className="h-2 w-2 animate-pulse rounded-full bg-primary"></div>
                                        {t("status_legend")}
                                    </div>

                                    <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
                                        {getStatusLegendItems(t).map(({ status, label, isUnassigned }) => (
                                            <div
                                                key={`${status}-${isUnassigned}`}
                                                className="group flex cursor-default items-center gap-1.5 rounded-lg p-1.5 transition-colors duration-200 hover:bg-muted/40"
                                            >
                                                <div
                                                    className={`h-2.5 w-2.5 rounded-full shadow-sm ${getStatusCircleColor(status, isUnassigned)}`}
                                                ></div>
                                                <span className="truncate text-xs font-medium text-foreground">
                                                    {label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="shrink-0 px-2 md:border-t md:border-border/30 md:pt-2 lg:border-t-0 lg:border-l lg:pl-3 lg:pt-0">
                                    <CurrentAgentsStatus userStatuses={userStatuses} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <AgentInformationPanel
                        isOpen={isAgentPanelOpen}
                        onOpenChange={setIsAgentPanelOpen}
                        assignments={assignments}
                        selectedAgent={selectedAgent}
                        selectedSeat={selectedSeat}
                        userStatuses={userStatuses}
                        setSelectedAgent={setSelectedAgent}
                        setSelectedSeat={setSelectedSeat}
                        setShowAddMembersModal={setShowAddMembersModal}
                        isLoading={false}
                        deleteAssignment={handleDeleteAssignment}
                        handleOpenSettingsModal={handleOpenSettingsModal}
                    />
                </div>
            </div>

            {/* Add Members Modal */}
            <AddMembersModal
                isOpen={showAddMembersModal}
                unassignedUsers={unassignedUsers}
                unassignedUsersTotal={unassignedUsersTotal}
                currentPage={currentPage}
                pageSize={10}
                userSearchQuery={userSearchQuery}
                isLoading={isLoadingModalUsers}
                showRoleSelection={false}
                onAdd={handleAddMembers}
                onClose={() => {
                    setShowAddMembersModal(false);
                    setSelectedSeat(null);
                }}
                onPageChange={setCurrentPage}
                onUserSearchChange={setUserSearchQuery}
            />

            <AgentSettingsDialog
                open={showSettingsModal}
                onOpenChange={setShowSettingsModal}
                userId={selectedAgent?.userId}
            />
        </div>
    );
}
